import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { ChevronLeft, ChevronRight, ArrowLeft, CheckCircle2, MessageSquare, Loader2, FileVideo, Image as ImageIcon, Calendar as CalendarIcon, Film } from 'lucide-react';
import { toast } from 'sonner';
import { contentCalendarAPI, isClientViewMode } from '../lib/api';
import { KindBadge } from './AdApprovalParts';
import { ChannelLabels } from './ChannelIcons';
import { InstagramPreview } from './InstagramPreview';

const TIPO_LABEL = { reel: 'Reel', post: 'Post', carrusel: 'Carrusel', story: 'Story', stories: 'Stories', video: 'Vídeo' };
export const tipoLabel = (t) => TIPO_LABEL[(t || 'post').toLowerCase()] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Post');
const fmtLongDate = (ymd) => {
  if (!ymd) return 'Sin fecha';
  const d = new Date(`${ymd.slice(0, 10)}T12:00:00`);
  const t = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const isVideo = (a) => (a?.kind || (a?.content_type || '').split('/')[0]) === 'video';

// Media ordenada: creatividad/publicación → portada → resto de material
export const useMedia = (post) => {
  const [media, setMedia] = useState(null);
  useEffect(() => {
    if (!post) return;
    let alive = true;
    setMedia(null);
    contentCalendarAPI.listAttachments(post.id).then((list) => {
      if (!alive) return;
      const rank = (a) => (a.id === post.color_portada_file_id ? 0 : a.id === post.portada_file_id ? 1 : 2);
      const label = (a) => (a.id === post.color_portada_file_id ? 'Creatividad' : a.id === post.portada_file_id ? 'Portada' : (a.original_filename || 'Material'));
      const raw = Array.isArray(list) ? list : (list?.attachments || []);
      const items = raw
        .filter((a) => ['image', 'video'].includes(a.kind || (a.content_type || '').split('/')[0]))
        .sort((a, b) => rank(a) - rank(b))
        .map((a) => ({ ...a, label: label(a), url: contentCalendarAPI.getAttachmentUrl(post.id, a.id) }));
      setMedia(items);
    }).catch(() => alive && setMedia([]));
    return () => { alive = false; };
  }, [post?.id, post?.portada_file_id, post?.color_portada_file_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return media;
};

function MediaViewer({ post, media }) {
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [post?.id]);
  if (media === null) return <div className="aspect-[4/5] max-h-[70vh] w-full rounded-xl bg-muted flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (media.length === 0) {
    return (
      <div className="w-full rounded-xl border border-dashed bg-muted/40 py-16 flex flex-col items-center justify-center text-muted-foreground" data-testid="review-no-media">
        <ImageIcon className="w-10 h-10 mb-2" />
        <p className="text-sm">Esta publicación aún no tiene imagen ni vídeo adjunto.</p>
      </div>
    );
  }
  const cur = media[Math.min(active, media.length - 1)];
  return (
    <div className="space-y-3" data-testid="review-media">
      <div className="w-full rounded-xl overflow-hidden bg-black/90 flex items-center justify-center" style={{ minHeight: 320 }}>
        {isVideo(cur) ? (
          <video key={cur.id} src={cur.url} controls playsInline className="max-h-[70vh] w-auto max-w-full" data-testid="review-media-video" />
        ) : (
          <img key={cur.id} src={cur.url} alt={cur.label} className="max-h-[70vh] w-auto max-w-full object-contain" data-testid="review-media-image" />
        )}
      </div>
      {media.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="review-media-thumbs">
          {media.map((m, i) => (
            <button key={m.id} onClick={() => setActive(i)} title={m.label}
              className={`relative shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${i === active ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100'}`}
              data-testid={`review-media-thumb-${i}`}>
              {isVideo(m) ? <div className="w-full h-full bg-muted flex items-center justify-center"><FileVideo className="w-6 h-6 text-muted-foreground" /></div>
                : <img src={m.url} alt={m.label} className="w-full h-full object-cover" />}
              <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] px-1 truncate">{m.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ApprovalListCard({ post, onOpen, selectable = false, selected = false, onToggle }) {
  const thumbId = post.color_portada_file_id || post.portada_file_id;
  const [broken, setBroken] = useState(false);
  const viewed = !!post.client_viewed_at;
  return (
    <Card className={`cursor-pointer hover:shadow-md hover:border-primary transition-[box-shadow,border-color] overflow-hidden ${selected ? 'border-emerald-500 ring-1 ring-emerald-500' : 'border-amber-300'}`} onClick={() => onOpen(post)} data-testid={`approval-item-${post.id}`}>
      <div className="flex">
        {selectable && (
          <div className="flex items-center pl-3" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={selected} disabled={!viewed} onCheckedChange={() => onToggle?.(post.id)}
              title={viewed ? 'Seleccionar para aprobar' : 'Ábrela y revísala antes de aprobarla'} data-testid={`approval-select-${post.id}`} />
          </div>
        )}
        <div className="w-28 h-28 shrink-0 bg-muted flex items-center justify-center">
          {thumbId && !broken
            ? <img src={contentCalendarAPI.getAttachmentUrl(post.id, thumbId)} alt="" className="w-full h-full object-cover" onError={() => setBroken(true)} />
            : thumbId
              ? <div className="relative w-full h-full"><video src={`${contentCalendarAPI.getAttachmentUrl(post.id, thumbId)}#t=0.5`} preload="metadata" muted playsInline className="w-full h-full object-cover" /><span className="absolute inset-0 flex items-center justify-center"><span className="rounded-full bg-black/60 p-1.5"><Film className="w-4 h-4 text-white" /></span></span></div>
              : <Film className="w-8 h-8 text-muted-foreground" />}
        </div>
        <CardContent className="p-3 min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <KindBadge kind="post" />
            {selectable && (viewed
              ? <span className="shrink-0 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700" data-testid={`approval-viewed-${post.id}`}>Revisada</span>
              : <span className="shrink-0 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-slate-100 text-slate-600" data-testid={`approval-unviewed-${post.id}`}>Sin revisar</span>)}
          </div>
          <p className="font-semibold text-sm truncate mt-1">{post.titulo || 'Sin título'}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{fmtLongDate(post.fecha)}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-xs">
            <span className="px-1.5 py-0.5 rounded bg-muted font-medium">{tipoLabel(post.tipo)}</span>
            <ChannelLabels post={post} emptyText="red social" testId={`approval-item-channels-${post.id}`} />
          </div>
          {post.copy && <p className="text-xs text-muted-foreground line-clamp-2 mt-1.5">{post.copy}</p>}
          <p className="text-xs text-primary font-medium mt-1.5">Revisar publicación →</p>
        </CardContent>
      </div>
    </Card>
  );
}

export default function ApprovalReview({ pending, reviewId, setReviewId, onApprove, approving, onUpdated, ObservationsThread, isClient = false, profile = null }) {
  const lastIndex = useRef(0);
  const idx = pending.findIndex((p) => p.id === reviewId);
  const post = idx >= 0 ? pending[idx] : null;
  const [showChanges, setShowChanges] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [view, setView] = useState('preview');
  const media = useMedia(post);

  useEffect(() => {
    if (idx >= 0) { lastIndex.current = idx; return; }
    // la publicación ya no está pendiente (aprobada / cambios) → pasar a la siguiente
    if (pending.length) setReviewId(pending[Math.min(lastIndex.current, pending.length - 1)].id);
    else setReviewId(null);
  }, [idx, pending, setReviewId]);
  useEffect(() => { setShowChanges(false); setText(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }, [reviewId]);
  useEffect(() => {
    if (!isClient || !post || post.client_viewed_at) return;
    contentCalendarAPI.markViewed(post.id).then((r) => { if (r?.ok) onUpdated({ ...post, client_viewed_at: r.client_viewed_at }); }).catch(() => {});
  }, [post?.id, isClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = useMemo(() => (d) => {
    const n = idx + d;
    if (n >= 0 && n < pending.length) setReviewId(pending[n].id);
  }, [idx, pending, setReviewId]);
  useEffect(() => {
    const h = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [go]);

  if (!post) return null;
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const updated = await contentCalendarAPI.requestChanges(post.id, t);
      toast.success('Cambios solicitados · el equipo lo revisará');
      onUpdated(updated);
    } catch (e) { toast.error(e?.message || 'Error solicitando cambios'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="approval-review">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => setReviewId(null)} data-testid="review-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Volver al listado</Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => go(-1)} disabled={idx <= 0} title="Anterior" data-testid="review-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium tabular-nums" data-testid="review-position">{idx + 1} de {pending.length}</span>
          <Button variant="outline" size="icon" onClick={() => go(1)} disabled={idx >= pending.length - 1} title="Siguiente" data-testid="review-next-btn"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <div className="p-4 bg-muted/30 space-y-3">
            <div className="flex rounded-lg border bg-background p-0.5 w-fit mx-auto text-xs" data-testid="review-view-toggle">
              <button onClick={() => setView('preview')} className={`px-3 py-1.5 rounded-md font-medium transition-colors ${view === 'preview' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid="review-view-preview">Cómo se verá en Instagram</button>
              <button onClick={() => setView('files')} className={`px-3 py-1.5 rounded-md font-medium transition-colors ${view === 'files' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid="review-view-files">Archivos originales</button>
            </div>
            {view === 'preview'
              ? (media === null ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div> : <InstagramPreview post={post} media={media} profile={profile} />)
              : <MediaViewer post={post} media={media} />}
          </div>
          <div className="p-5 space-y-4 flex flex-col">
            <div>
              <h2 className="text-xl font-bold" data-testid="review-title">{post.titulo || 'Sin título'}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-sm">
                <div className="rounded-lg border p-2"><p className="text-[11px] text-muted-foreground uppercase">Tipo</p><p className="font-semibold" data-testid="review-tipo">{tipoLabel(post.tipo)}</p></div>
                <div className="rounded-lg border p-2"><p className="text-[11px] text-muted-foreground uppercase flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> Publicación</p><p className="font-semibold text-xs leading-5" data-testid="review-fecha">{fmtLongDate(post.fecha)}</p></div>
                <div className="rounded-lg border p-2"><p className="text-[11px] text-muted-foreground uppercase">Canal</p><div className="mt-0.5"><ChannelLabels post={post} emptyText="red social" testId="review-canal" /></div></div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Copy</p>
              <div className="text-sm whitespace-pre-wrap bg-muted/30 rounded-lg p-3 border max-h-[40vh] overflow-y-auto" data-testid="review-copy">{post.copy || <span className="italic text-muted-foreground">Sin copy</span>}</div>
            </div>
            {ObservationsThread && <ObservationsThread observaciones={post.observaciones} />}

            <div className="mt-auto pt-2 space-y-3 border-t">
              {showChanges ? (
                <div className="space-y-2" data-testid="review-changes-form">
                  <p className="text-sm font-medium">¿Qué te gustaría cambiar?</p>
                  <Textarea autoFocus rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ej: cambiar la foto de portada, corregir el precio del copy..." data-testid="review-changes-text" />
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" onClick={() => { setShowChanges(false); setText(''); }} data-testid="review-changes-cancel">Cancelar</Button>
                    <Button onClick={send} disabled={!text.trim() || sending} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="review-changes-send">
                      {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />} Enviar cambios
                    </Button>
                  </div>
                </div>
              ) : isClientViewMode() ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="review-readonly-note">Modo vista de cliente: aquí el cliente ve los botones Aprobar y Solicitar cambios. Tú solo puedes mirar.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button onClick={() => onApprove(post)} disabled={approving === post.id} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11" data-testid="review-approve-btn">
                    {approving === post.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Aprobar
                  </Button>
                  <Button variant="outline" onClick={() => setShowChanges(true)} className="border-amber-300 text-amber-700 hover:bg-amber-50 h-11" data-testid="review-request-changes-btn">
                    <MessageSquare className="w-4 h-4 mr-2" /> Solicitar cambios
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
